import React from 'react';
import { Droplets, Waves, Home, CheckCircle2 } from 'lucide-react';
import { UTILITY_SOURCE_OPTIONS, UTILITY_SOURCE_FIELD_LABEL, UTILITY_SOURCE_LABEL, isOffGridSource } from './mlsServiceTypes';
import { labelCls } from './MlsFields';

const ICON = { public_water: Droplets, private_well: Home, public_sewer: Waves, septic_tank: Home };

/* MLS-UTILITY-WELL-SEPTIC-001 (+002): mandatory segmented radio — Water Source (Public Water | Private Well) /
   Sewer Type (Public Sewer | Septic Tank). Nothing is pre-selected: the processor's click is the confirmation.
   Same height as the neighbouring fields; `error` (truthy) only turns the group red — the Required label is the message. */
export const MlsUtilitySource = ({ utype, value, disabled, onChange, prefix, error = null, className = '' }) => {
  const options = UTILITY_SOURCE_OPTIONS[utype];
  if (!options) return null;
  const invalid = !!error && !value && !disabled;
  return (
    <div className={className} data-testid={`${prefix}-utility-source`} data-value={value || undefined} data-invalid={invalid || undefined}>
      <label className={`${labelCls} flex items-center justify-between gap-2`}>
        <span>{UTILITY_SOURCE_FIELD_LABEL[utype]} <span className="text-rose-500" aria-hidden="true">*</span></span>
        {!value && !disabled && (
          <span className={`normal-case tracking-normal font-semibold text-[10px] ${invalid ? 'text-rose-600' : 'text-amber-600'}`} data-testid={`${prefix}-utility-source-required`}>Required</span>
        )}
      </label>
      <div role="radiogroup" aria-label={UTILITY_SOURCE_FIELD_LABEL[utype]} aria-required="true" aria-invalid={invalid || undefined}
        className={`mls-source-group grid grid-cols-2 h-8 rounded-md border bg-white p-0.5 gap-0.5 transition-colors ${invalid ? 'border-rose-400 ring-2 ring-rose-100' : value ? 'border-slate-300' : 'border-amber-300'}`}>
        {options.map(([k, label]) => {
          const on = value === k;
          const off = isOffGridSource(k);
          const Icon = ICON[k];
          const T = off
            ? { on: 'bg-emerald-600 text-white shadow-sm', idle: 'text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 active:bg-emerald-100', ring: 'focus-visible:ring-emerald-500', dot: 'group-hover:border-emerald-500' }
            : { on: 'bg-blue-600 text-white shadow-sm', idle: 'text-slate-700 hover:bg-blue-50 hover:text-blue-800 active:bg-blue-100', ring: 'focus-visible:ring-blue-500', dot: 'group-hover:border-blue-500' };
          return (
            <button key={k} type="button" role="radio" aria-checked={on} disabled={disabled}
              onClick={() => !disabled && !on && onChange(k)}
              className={`mls-source-opt group inline-flex items-center justify-center gap-1.5 rounded px-1.5 text-[11px] font-semibold whitespace-nowrap cursor-pointer select-none
                transition-[background-color,color,box-shadow] duration-150 outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${T.ring} disabled:cursor-default disabled:opacity-60
                ${on ? T.on : T.idle}`}
              data-testid={`${prefix}-utility-source-${k}`}>
              {/* visible radio indicator */}
              <span aria-hidden="true" className={`grid place-items-center h-3.5 w-3.5 rounded-full border-2 transition-colors ${on ? 'border-white bg-white/20' : `border-slate-400 bg-white ${T.dot}`}`}>
                {on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
              </span>
              <Icon className="w-3 h-3" />{label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* QC-visible indicator for a confirmed well / septic entry (tab, header, summary) */
export const OffGridBadge = ({ value, testId, compact = false }) => {
  if (!isOffGridSource(value)) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold normal-case tracking-normal ${compact ? 'text-[9px] px-1 py-px' : 'text-[10px] px-1.5 py-px'}`}
      title={`${UTILITY_SOURCE_LABEL[value]} — confirmed by processor`} data-testid={testId}>
      <CheckCircle2 className="w-2.5 h-2.5" />{UTILITY_SOURCE_LABEL[value]}
    </span>
  );
};
